// EndpointParser.js - Simplified and Fixed for proper endpoint detection
import logger from '../../services/logging/logger';

// OPTIMIZATION: Extract magic numbers to named constants
const CONSTANTS = {
  MIN_CONTENT_LENGTH: 10, // Minimum content length for valid files
  MAX_FEATURE_NAME_LENGTH: 30, // Maximum length for generated feature names
  SEARCH_WINDOW_SIZE: 2000, // Size of content window for JSON search
  VALID_HTTP_METHODS: ['GET', 'POST', 'PUT', 'DELETE', 'PATCH', 'HEAD', 'OPTIONS']
};

class EndpointParser {

// EndpointParser.js

/**
 * Reset all static state - MUST be called before starting a new analysis
 */
static reset() {
  logger.info('Resetting EndpointParser state', {
    hadReportedEndpoints: !!this.reportedEndpoints,
    reportedCount: this.reportedEndpoints?.size || 0,
    hadPhase1Data: !!this.globalPhase1Data,
    wasPhase1Extracted: !!this.phase1Extracted
  });

  // Clear reported endpoints set
  this.reportedEndpoints = new Set();

  // Clear Phase 1 data
  this.globalPhase1Data = null;
  this.phase1Extracted = false;

  logger.info('EndpointParser reset complete - ready for new analysis');
}
  
/**
 * Initialize endpoint tracking set
 */
static initializeReportedEndpoints() {
  if (!this.reportedEndpoints) {
    this.reportedEndpoints = new Set();
  }
}

/**
 * Process Phase 1 global data extraction
 * @param {string} content - Accumulated content to extract from
 */
static processPhase1Data(content) {
  // Once fully extracted (sourceSpec + schemas both present) skip all further work.
  if (this.phase1Extracted) return;

  // Guard: skip if none of the Phase 1 indicators are present (performance short-circuit)
  if (
    !content.includes('Phase 1') &&
    !content.includes('Global Source Specification') &&
    !content.includes('globalSourceSpec') &&
    !content.includes('Global Schema Registry') &&
    !content.includes('globalSchemas')
  ) {
    return;
  }

  const globalData = this.extractPhase1Data(content);
  if (!globalData) {
    logger.warn('extractPhase1Data returned null');
    return;
  }

  // Always update with the latest extraction (progressively more complete)
  this.globalPhase1Data = globalData;

  // Only mark as "fully extracted" if we have BOTH sections
  const hasSourceSpec = globalData.sourceSpec && globalData.sourceSpec.info;
  const hasSchemas = Object.keys(globalData.schemas).length > 0;

  if (hasSourceSpec && hasSchemas) {
    this.phase1Extracted = true;
    logger.info('Phase 1 fully extracted', {
      hasSourceSpec,
      schemaCount: Object.keys(globalData.schemas).length,
      schemaNames: Object.keys(globalData.schemas)
    });
  } else {
    logger.debug('Phase 1 partial - waiting for more data', {
      hasSourceSpec,
      hasSchemas,
      contentLength: content.length
    });
  }
}

/**
 * Parse endpoint metadata from JSON mapping data
 * @param {Object} mappingData - Parsed mapping data
 * @returns {Object} Extracted metadata
 */
static parseEndpointMetadata(mappingData) {
  const operationDef = mappingData.operationDefinition || {};

  return {
    parameters: operationDef.parameters || [],
    responses: operationDef.responses || {},
    hasRequestBody: !!(operationDef.requestBody)
  };
}

/**
 * Attach Phase 1 global data to mapping data
 * @param {Object} mappingData - Mapping data to attach to
 * @param {string} method - HTTP method
 * @param {string} path - Endpoint path
 */
static attachPhase1GlobalData(mappingData, method, path) {
  if (!this.globalPhase1Data) {
    return;
  }

  mappingData.globalSourceSpec = this.globalPhase1Data.sourceSpec;
  mappingData.globalSchemas = this.globalPhase1Data.schemas;

  logger.debug('Attached Phase 1 data to endpoint', {
    endpoint: `${method} ${path}`,
    hasSourceSpec: !!this.globalPhase1Data.sourceSpec.info,
    schemaCount: Object.keys(this.globalPhase1Data.schemas).length
  });
}

/**
 * Build endpoint object from parsed data
 * @param {string} method - HTTP method
 * @param {string} path - Cleaned endpoint path
 * @param {Object} mappingData - Parsed mapping data
 * @param {Object} metadata - Extracted metadata
 * @returns {Object} Complete endpoint object
 */
static buildEndpointObject(method, path, mappingData, metadata) {
  return {
    method: method,
    path: path,
    displayPath: path,
    context: '',
    description: `${method} ${path}`,
    operationId: mappingData?.operationDefinition?.operationId || null,
    summary: mappingData?.operationDefinition?.summary || null,
    parameters: this.extractParameterInfo(metadata.parameters),
    responses: this.extractResponseInfo(metadata.responses),
    hasRequestBody: metadata.hasRequestBody,
    responseCount: Object.keys(metadata.responses).length,
    paramCount: metadata.parameters.length,
    mappingData: mappingData,
    discoveredAt: new Date().toISOString(),
    hasCompleteMapping: true,
    isHeaderOnly: false
  };
}

/**
 * Process a single endpoint match
 * @param {Array} match - Regex match result
 * @param {Function} onEndpointFound - Callback for discovered endpoints
 * @param {Array} discoveredEndpoints - Array to collect endpoints
 */
static processEndpointMatch(match, onEndpointFound, discoveredEndpoints) {
  const method = match[1].trim().toUpperCase();
  const rawPath = match[2].trim();
  const jsonContent = match[3].trim();

  // Validate HTTP method
  if (!this.isValidHttpMethod(method)) return;

  // Clean up the path
  const cleanedPath = this.cleanEndpointPath(rawPath);

  // Parse the JSON to extract metadata
  let mappingData = null;
  let metadata = { parameters: [], responses: {}, hasRequestBody: false };

  try {
    mappingData = JSON.parse(jsonContent);
    metadata = this.parseEndpointMetadata(mappingData);

    // Attach Phase 1 global data if available
    this.attachPhase1GlobalData(mappingData, method, cleanedPath);

  } catch (err) {
    logger.warn('Could not parse JSON for endpoint', { method, path: cleanedPath });
  }

  // Create unique endpoint key
  const endpointKey = `${method}:${cleanedPath}`;

  if (!this.reportedEndpoints.has(endpointKey)) {
    const endpoint = this.buildEndpointObject(method, cleanedPath, mappingData, metadata);

    logger.info('Discovered complete endpoint', { method, path: cleanedPath });

    if (onEndpointFound) {
      onEndpointFound(endpoint);
    }

    this.reportedEndpoints.add(endpointKey);
    discoveredEndpoints.push(endpoint);
  }
}

/**
 * Incrementally parse endpoints from accumulated content
 * Main coordinator function that delegates to focused helpers
 * @param {string} accumulatedContent - Streaming content to parse
 * @param {Function} onEndpointFound - Callback for each discovered endpoint
 * @returns {Array} Discovered endpoints
 */
static parseEndpointsIncrementally(accumulatedContent, onEndpointFound) {
  logger.info('Incremental parsing', { contentLength: accumulatedContent.length });

  // Initialize tracking set
  this.initializeReportedEndpoints();

  // Process Phase 1 global data
  this.processPhase1Data(accumulatedContent);

  const discoveredEndpoints = [];

  // Look for endpoints with JSON blocks
  const endpointWithJsonPattern = /\*\*\s*([A-Z]+)\s+([^\n\*]+?)(?:\s+Mapping)?\*\*[\s\S]*?```json\s*([\s\S]*?)\s*```/g;
  const matches = [...accumulatedContent.matchAll(endpointWithJsonPattern)];

  // Process each endpoint match
  for (const match of matches) {
    this.processEndpointMatch(match, onEndpointFound, discoveredEndpoints);
  }

  return discoveredEndpoints;
}

// EndpointParser.js - Fix the extractPhase1Data method

static extractPhase1Data(content) {
  try {
    const phase1Data = {
      sourceSpec: {
        info: null,
        servers: []
      },
      schemas: {}
    };
    
    logger.debug('Extracting Phase 1 data', { contentLength: content.length });
    
    // Look for Global Source Specification - try multiple heading patterns
    const sourceSpecPatterns = [
      // Bold markdown (original expected format)
      /\*\*Global Source Specification\*\*[\s\S]*?```json\s*([\s\S]*?)\s*```/i,
      /Global Source Specification[:\s]*```json\s*([\s\S]*?)\s*```/i,
      /Global Source Specification[:\s]*\n\s*```json\s*([\s\S]*?)\s*```/i,
      // Markdown headings (##, ###)
      /#{1,4}\s*Global Source Specification[\s\S]*?```json\s*([\s\S]*?)\s*```/i,
      // "API Info" / "API Overview" heading variants
      /#{1,4}\s*API\s+(?:Info|Information|Overview|Source\s+Spec(?:ification)?)[\s\S]*?```json\s*([\s\S]*?)\s*```/i,
      /\*\*API\s+(?:Info|Information|Overview)\*\*[\s\S]*?```json\s*([\s\S]*?)\s*```/i,
    ];

    for (const pattern of sourceSpecPatterns) {
      const match = content.match(pattern);
      if (match) {
        try {
          const parsed = JSON.parse(match[1]);
          phase1Data.sourceSpec = parsed.globalSourceSpec || parsed;
          logger.debug('Extracted source spec via heading pattern');
          break;
        } catch (e) {
          logger.warn('Could not parse source spec with pattern', { pattern: pattern.toString() });
        }
      }
    }

    // Look for Global Schema Registry - try multiple heading patterns
    const schemaPatterns = [
      // Bold markdown (original expected format)
      /\*\*Global Schema Registry\*\*[\s\S]*?```json\s*([\s\S]*?)\s*```/i,
      /Global Schema Registry[:\s]*```json\s*([\s\S]*?)\s*```/i,
      /Global Schema Registry[:\s]*\n\s*```json\s*([\s\S]*?)\s*```/i,
      // Markdown headings
      /#{1,4}\s*Global Schema Registry[\s\S]*?```json\s*([\s\S]*?)\s*```/i,
      /#{1,4}\s*(?:Global\s+)?Schemas?[\s\S]*?```json\s*([\s\S]*?)\s*```/i,
    ];

    for (const pattern of schemaPatterns) {
      const match = content.match(pattern);
      if (match) {
        try {
          const parsed = JSON.parse(match[1]);
          phase1Data.schemas = parsed.globalSchemas || parsed;
          logger.debug('Extracted schemas via heading pattern', {
            schemaCount: Object.keys(phase1Data.schemas).length
          });
          break;
        } catch (e) {
          logger.warn('Could not parse schemas with pattern', { pattern: pattern.toString(), error: e.message });
        }
      }
    }

    // FALLBACK: If heading-based patterns failed, scan all JSON blocks in the content.
    // The LLM sometimes omits the section headings and embeds the data directly in JSON.
    const headingSourceSpecFound = !!(phase1Data.sourceSpec && phase1Data.sourceSpec.info);
    const headingSchemaFound = Object.keys(phase1Data.schemas).length > 0;

    if (!headingSourceSpecFound || !headingSchemaFound) {
      const allJsonBlocks = [...content.matchAll(/```json\s*([\s\S]*?)\s*```/g)];

      for (const block of allJsonBlocks) {
        try {
          const parsed = JSON.parse(block[1]);

          if (!headingSourceSpecFound && !(phase1Data.sourceSpec && phase1Data.sourceSpec.info)) {
            // Wrapped as { globalSourceSpec: { info: ... } }
            if (parsed.globalSourceSpec?.info?.title) {
              phase1Data.sourceSpec = parsed.globalSourceSpec;
              logger.debug('Extracted source spec from globalSourceSpec key in JSON block');
            }
            // OpenAPI-style: root object has info.title + version or servers
            else if (parsed.info?.title && (parsed.info?.version !== undefined || Array.isArray(parsed.servers))) {
              phase1Data.sourceSpec = parsed;
              logger.debug('Extracted source spec from OpenAPI info in JSON block');
            }
          }

          if (!headingSchemaFound && Object.keys(phase1Data.schemas).length === 0) {
            // Wrapped as { globalSchemas: { ... } }
            if (parsed.globalSchemas && typeof parsed.globalSchemas === 'object' && !Array.isArray(parsed.globalSchemas)) {
              phase1Data.schemas = parsed.globalSchemas;
              logger.debug('Extracted schemas from globalSchemas key in JSON block');
            }
          }
        } catch (e) {
          continue;
        }
      }
    }

    // Debug: Log what we found
    const hasSourceSpec = phase1Data.sourceSpec && phase1Data.sourceSpec.info;
    const hasSchemas = Object.keys(phase1Data.schemas).length > 0;

    logger.debug('Phase 1 extraction summary', {
      hasSourceSpec,
      hasSchemas,
      schemaCount: Object.keys(phase1Data.schemas).length,
      contentIncludesSchemaRegistry: content.includes('Global Schema Registry')
    });

    // Only return if we found something
    if (hasSourceSpec || hasSchemas) {
      logger.info('Extracted Phase 1 data', {
        hasSourceSpec: !!hasSourceSpec,
        schemaCount: Object.keys(phase1Data.schemas).length
      });
      return phase1Data;
    } else {
      logger.warn('No Phase 1 data found');
    }
    
  } catch (err) {
    logger.error('Error in extractPhase1Data', { error: err.message });
  }
  
  return null;
}

  // New method to clean endpoint paths
  static cleanEndpointPath(rawPath) {
    let cleanedPath = rawPath.trim();
    
    logger.debug('Cleaning path', { rawPath });
    
    // Remove "Mapping" word (case insensitive)
    cleanedPath = cleanedPath.replace(/\s*mapping\s*/gi, ' ').trim();
    
    // Remove colons at the end or standalone colons
    cleanedPath = cleanedPath.replace(/:+$/, '').trim();
    
    // Remove standalone colons with spaces around them
    cleanedPath = cleanedPath.replace(/\s*:\s*$/, '').trim();
    
    // Clean up multiple spaces
    cleanedPath = cleanedPath.replace(/\s+/g, ' ').trim();
    
    // Remove markdown formatting
    cleanedPath = cleanedPath.replace(/^\*+|\*+$/g, '').trim();
    
    logger.debug('Cleaned result', { original: rawPath, cleaned: cleanedPath });
    
    return cleanedPath;
  }
  
  // New method to parse path with context
static parsePathWithContext(rawPath) {
  // First clean the path
  let path = this.cleanEndpointPath(rawPath);
  let context = '';
  let displayPath = path;
  
  logger.debug('Parsing path with context', { rawPath, cleaned: path });
  
  // Handle descriptive patterns - be more flexible with matching
  if (path.toLowerCase().includes('by type')) {
    context = 'by-type';
    // Extract just the base path
    path = path.replace(/\s*\([^)]*\).*$/, '').trim();
    path = path.replace(/\s+by\s+type.*$/i, '').trim();
    displayPath = `${path} (by type)`;
  } else if (path.toLowerCase().includes('by keyword')) {
    context = 'by-keyword';
    // Extract just the base path  
    path = path.replace(/\s*\([^)]*\).*$/, '').trim();
    path = path.replace(/\s+by\s+keyword.*$/i, '').trim();
    displayPath = `${path} (by keyword)`;
  } else {
    displayPath = path;
  }
  
  // Ensure path starts with /
  if (path && !path.startsWith('/') && !path.includes(' ')) {
    path = '/' + path;
  }
  
  logger.debug('Parsed result', { originalPath: rawPath, path, context, displayPath });
  
  return { path, context, displayPath };
}
  
  // Updated to handle display path and context
  static createUserFriendlyEndpointOriginal(method, path, mappingData, displayPath, context) {
    // Clean the paths
    const cleanedPath = this.cleanEndpointPath(path);
    const cleanedDisplayPath = this.cleanEndpointPath(displayPath);
    
    const operationDef = mappingData.operationDefinition || {};
    const resolvedSchemas = mappingData.resolvedSchemas || {};
    
    // Extract parameters
    const parameters = operationDef.parameters || [];
    const paramCount = parameters.length;
    
    // Extract responses
    const responses = resolvedSchemas.responses || operationDef.responses || {};
    const responseCount = Object.keys(responses).length;
    
    // Extract request body
    const hasRequestBody = !!(resolvedSchemas.requestBody || operationDef.requestBody);
    
    logger.debug('Metadata debug for endpoint', {
      method,
      path: cleanedDisplayPath,
      paramCount,
      hasRequestBody,
      responseCount,
      operationId: operationDef.operationId,
      parametersArray: parameters,
      responsesObj: responses
    });
    
    // Create endpoint object
    const endpoint = {
      method: method,
      path: cleanedPath,
      displayPath: cleanedDisplayPath,
      context: context,
      description: `${method} ${cleanedDisplayPath}`,
      operationId: operationDef.operationId || null,
      summary: operationDef.summary || null,
      tags: operationDef.tags || [],
      parameters: this.extractParameterInfo(parameters),
      requestBody: this.extractRequestBodyInfo(resolvedSchemas.requestBody || operationDef.requestBody),
      responses: this.extractResponseInfo(responses),
      
      // Explicit counts for UI
      hasRequestBody: hasRequestBody,
      responseCount: responseCount,
      paramCount: paramCount,
      
      mappingData: mappingData,
      discoveredAt: new Date().toISOString(),
      hasCompleteMapping: true,
      isHeaderOnly: false
    };
    
    return endpoint;
  }

  // Original parameter extraction
static extractParameterInfo(parameters) {
  // Handle both array and object formats
  if (!parameters) return [];
  
  // If it's already an array, use it directly
  if (Array.isArray(parameters)) {
    return parameters.map(param => ({
      name: param.name,
      location: param.in,
      required: param.required || false,
      type: param.schema?.type || 'unknown',
      description: param.description || null
    }));
  }
  
  // If it's an object (like {}), convert to array
  if (typeof parameters === 'object') {
    return Object.values(parameters).map(param => ({
      name: param.name,
      location: param.in,
      required: param.required || false,
      type: param.schema?.type || 'unknown',
      description: param.description || null
    }));
  }
  
  // Fallback for unexpected formats
  return [];
}

  // Original request body extraction
  static extractRequestBodyInfo(requestBodySchema) {
    if (!requestBodySchema) return null;
    
    return {
      required: true,
      contentType: 'application/json',
      schema: this.simplifySchema(requestBodySchema),
      fieldCount: this.countSchemaFields(requestBodySchema)
    };
  }

  // Original response extraction
  static extractResponseInfo(responses) {
    const responseInfo = {};
    
    Object.keys(responses).forEach(statusCode => {
      const response = responses[statusCode];
      responseInfo[statusCode] = {
        description: response.description || 'OK',
        schema: response.schema ? this.simplifySchema(response.schema) : null,
        fieldCount: response.schema ? this.countSchemaFields(response.schema) : 0
      };
    });
    
    return responseInfo;
  }

  // Original schema simplification
  static simplifySchema(schema) {
    if (!schema || !schema.properties) {
      return { type: schema?.type || 'unknown' };
    }
    
    const simplified = {
      type: 'object',
      fieldNames: Object.keys(schema.properties),
      fieldTypes: {}
    };
    
    Object.keys(schema.properties).forEach(fieldName => {
      const field = schema.properties[fieldName];
      simplified.fieldTypes[fieldName] = field.type || 'unknown';
    });
    
    return simplified;
  }

  // Original schema field counting
  static countSchemaFields(schema) {
    if (!schema || !schema.properties) return 0;
    return Object.keys(schema.properties).length;
  }

  /**
   * Check if a method is a valid HTTP method
   * OPTIMIZATION: Use Set for O(1) lookup instead of array.includes()
   */
  static isValidHttpMethod(method) {
    // Lazy initialization of Set for better performance
    if (!this._validMethodsSet) {
      this._validMethodsSet = new Set(CONSTANTS.VALID_HTTP_METHODS);
    }
    return this._validMethodsSet.has(method.toUpperCase());
  }
  
  static isValidPath(path) {
    if (!path || path.length === 0) return false;
    
    // Must start with /
    if (!path.startsWith('/')) return false;
    
    // Should not contain spaces (unless in path parameters)
    if (path.includes(' ') && !path.includes('{')) return false;
    
    // Basic path format validation
    return /^\/[a-zA-Z0-9\/_\-{}]*$/.test(path);
  }
  
  /**
   * Find mapping data for an endpoint
   * OPTIMIZATION: Use named constant for search window size
   */
  static findMappingData(content, method, path, headerIndex) {
    // Look for JSON block after the header
    const afterHeader = content.slice(headerIndex);
    const nextHeaderMatch = afterHeader.slice(1).search(/\*\*[A-Z]+\s+/);

    // Limit search to content before next header
    const searchContent = nextHeaderMatch > 0 ?
      afterHeader.slice(0, nextHeaderMatch + 1) :
      afterHeader.slice(0, CONSTANTS.SEARCH_WINDOW_SIZE);

    const jsonMatch = searchContent.match(/```json\s*([\s\S]*?)\s*```/);

    if (jsonMatch) {
      try {
        const mappingData = JSON.parse(jsonMatch[1]);

        // Validate this JSON is for our endpoint
        if (this.validateMappingData(mappingData, method, path)) {
          return mappingData;
        }
      } catch (err) {
        return null;
      }
    }

    return null;
  }
  
  static validateMappingData(mappingData, method, path) {
    const endpoint = mappingData.endpoint;
    if (!endpoint) return false;
    
    return endpoint.method === method && endpoint.path === path;
  }
  
  static extractEndpointsFromJsonBlocks(content, onEndpointFound) {
    // Only parse JSON blocks if we haven't found any headers yet
    const hasHeaders = /\*\*[A-Z]+\s+[^\*]+/.test(content);
    
    if (hasHeaders) {
      logger.debug('Headers found - skipping JSON block extraction to preserve gradual discovery');
      return;
    }
    
    logger.debug('No headers found - falling back to JSON block extraction');
    
    const jsonBlocks = [...content.matchAll(/```json\s*([\s\S]*?)\s*```/g)];
    
    for (const block of jsonBlocks) {
      try {
        const data = JSON.parse(block[1]);
        
        if (data.endpoint && data.endpoint.method && data.endpoint.path) {
          const method = data.endpoint.method;
          const rawPath = data.endpoint.path;
          const cleanedPath = this.cleanEndpointPath(rawPath);
          const endpointKey = `${method}:${cleanedPath}`;
          
          if (!this.reportedEndpoints.has(endpointKey)) {
            const userFriendlyEndpoint = this.createUserFriendlyEndpointOriginal(method, cleanedPath, data, cleanedPath, '');
            
            logger.info('Discovered endpoint from JSON', { method, path: cleanedPath });
            
            if (onEndpointFound) {
              onEndpointFound(userFriendlyEndpoint);
            }
            
            this.reportedEndpoints.add(endpointKey);
          }
        }
      } catch (err) {
        continue;
      }
    }
  }

  // Original methods kept the same
  static parseEndpointMappingsFromResponse(content) {
    logger.info('Parsing complete endpoint mappings from content');
    const endpoints = this.parseEndpointsIncrementally(content, null);

    logger.info('Total complete mappings parsed', { count: endpoints.length });
    return endpoints;
  }

  static createMappingMessage() {
    return "provide pre test mapping";
  }

  /**
   * Detect a <mapper_handoff> XML block in the mapping buffer.
   * The backend sends this at the end of each batch when mapping large API specs.
   * If continuation_required is true, the frontend should send a follow-up message
   * echoing back all the fields the backend provided (promptId, businessProcess, etc).
   *
   * All fields are extracted dynamically from the XML — nothing is hardcoded.
   * The backend controls what gets sent back.
   *
   * @param {string} buffer - The accumulated mapping response buffer
   * @returns {Object|null} Parsed handoff data, or null if no handoff found
   */
  static detectMapperHandoff(buffer) {
    if (!buffer || !buffer.includes('<mapper_handoff>')) return null;

    try {
      const handoffMatch = buffer.match(/<mapper_handoff>([\s\S]*?)<\/mapper_handoff>/);
      if (!handoffMatch) return null;

      const xml = handoffMatch[1];

      // Extract all known fields dynamically from the XML
      const extractField = (tag) => {
        const match = xml.match(new RegExp(`<${tag}>(.*?)</${tag}>`));
        return match?.[1]?.trim() || null;
      };

      const extractBool = (tag) => {
        const val = extractField(tag);
        return val?.toLowerCase() === 'true';
      };

      const handoff = {
        // Core continuation fields
        lastMappedPath: extractField('path') || '',
        lastMappedMethod: extractField('method') || '',
        globalsEmitted: extractBool('globals_emitted'),
        continuationRequired: extractBool('continuation_required'),
        // Backend-controlled fields — echoed back in the continuation message
        promptId: extractField('prompt_id') || null,
        businessProcess: extractField('business_process') || null
      };

      logger.info('Mapper handoff detected', {
        lastMapped: `${handoff.lastMappedMethod} ${handoff.lastMappedPath}`,
        continuationRequired: handoff.continuationRequired,
        globalsEmitted: handoff.globalsEmitted,
        promptId: handoff.promptId,
        businessProcess: handoff.businessProcess
      });

      return handoff;
    } catch (err) {
      logger.warn('Failed to parse mapper_handoff XML', { error: err.message });
      return null;
    }
  }

// EndpointParser.js
static createTestConfigMessage(endpoint) {
  if (!endpoint.mappingData) {
    throw new Error('Endpoint missing mapping data');
  }
  
  // ✅ TRY TO GET FRESH PHASE 1 DATA FROM THE CLASS-LEVEL STORAGE
  // This ensures we get the latest extracted data even if the endpoint was stored earlier
  const globalSourceSpec = this.globalPhase1Data?.sourceSpec || endpoint.mappingData.globalSourceSpec || {};
  const globalSchemas = this.globalPhase1Data?.schemas || endpoint.mappingData.globalSchemas || {};
  
  logger.info('Creating test config message', {
    hasGlobalSourceSpec: Object.keys(globalSourceSpec).length > 0,
    hasGlobalSchemas: Object.keys(globalSchemas).length > 0,
    schemaCount: Object.keys(globalSchemas).length,
    schemaNames: Object.keys(globalSchemas)
  });
  
  // Check if we have Phase 1 data
  const hasPhase1Data = Object.keys(globalSourceSpec).length > 0 || 
                        Object.keys(globalSchemas).length > 0;
  
  // ✅ CREATE A CLEAN COPY OF MAPPING DATA WITHOUT PHASE 1 DATA
  const cleanMappingData = JSON.parse(JSON.stringify(endpoint.mappingData));
  delete cleanMappingData.globalSourceSpec;
  delete cleanMappingData.globalSchemas;
  
  // Build the message
  let message = 'provide test config for below only:\n';
  
  // Add Phase 1 data if it exists
  if (hasPhase1Data) {
    if (Object.keys(globalSourceSpec).length > 0) {
      message += '\nGlobal Source Specification:\n';
      message += JSON.stringify({ globalSourceSpec }, null, 2);
      message += '\n';
    }
    
    if (Object.keys(globalSchemas).length > 0) {
      message += '\nGlobal Schema Registry:\n';
      message += JSON.stringify({ globalSchemas }, null, 2);
      message += '\n';
    }
  }
  
  // ✅ ADD THE CLEAN ENDPOINT MAPPING DATA (without duplicate Phase 1 data)
  message += '\nEndpoint field:\n';
  message += `${endpoint.method} ${endpoint.path} Mapping\n`;
  message += JSON.stringify(cleanMappingData, null, 2);
  
  return message;
}

// NEW: Create permutation generation message
static createPermutationMessage(endpoint, testConfigFiles) {
  logger.info('CREATING PERMUTATION MESSAGE', { method: endpoint.method, path: endpoint.path });

  // OPTIMIZATION: Build a Map keyed by suffix patterns in a single O(n) pass,
  // replacing four separate O(n) find() calls with four O(1) Map lookups.
  // Filenames are prefixed with endpoint name (e.g. POST_Generate_OTP_request_data.csv)
  const FILE_SUFFIX_KEYS = ['request_data.csv', 'response_data.csv', 'request_schema.json', 'response_schema.json'];
  const fileBySuffix = new Map();
  for (const f of testConfigFiles) {
    for (const suffix of FILE_SUFFIX_KEYS) {
      if (!fileBySuffix.has(suffix) && f.filename.includes(suffix)) {
        fileBySuffix.set(suffix, f);
      }
    }
  }
  const requestDataFile = fileBySuffix.get('request_data.csv');
  const responseDataFile = fileBySuffix.get('response_data.csv');
  const requestSchemaFile = fileBySuffix.get('request_schema.json');
  const responseSchemaFile = fileBySuffix.get('response_schema.json');

  if (!requestDataFile || !responseDataFile) {
    logger.error('Missing required CSV files for permutation generation');
    throw new Error('Missing request or response data CSV files');
  }

  // Build the message
  let message = `Generate test permutations for ${endpoint.method} ${endpoint.path}\n\n`;

  // Add request data CSV
  message += '## request_data.csv\n';
  message += '```csv\n';
  message += requestDataFile.content;
  message += '\n```\n\n';

  // Add response data CSV
  message += '## response_data.csv\n';
  message += '```csv\n';
  message += responseDataFile.content;
  message += '\n```\n\n';

  // Add request schema (if available)
  if (requestSchemaFile) {
    message += '## request_schema.json\n';
    message += '```json\n';
    message += requestSchemaFile.content;
    message += '\n```\n\n';
  } else {
    message += '## request_schema.json\n';
    message += '```json\n';
    message += 'null\n';
    message += '```\n\n';
  }

  // Add response schema
  if (responseSchemaFile) {
    message += '## response_schema.json\n';
    message += '```json\n';
    message += responseSchemaFile.content;
    message += '\n```\n';
  } else {
    message += '## response_schema.json\n';
    message += '```json\n';
    message += 'null\n';
    message += '```\n';
  }

  logger.info('PERMUTATION MESSAGE CREATED');
  return message;
}

// Extract permutation files from response using regex literals
static extractPermutationFromResponse(content) {
  logger.info('EXTRACTING PERMUTATION FILES FROM CONTENT', { contentLength: content.length });
  logger.debug('PERMUTATION CONTENT PREVIEW', { first500: content.substring(0, 500) });

  const files = [];

  // Helper: clean CSV content by finding the header row and filtering non-CSV lines
  // AI sometimes includes descriptive text inside code blocks
  const cleanCSVContent = (rawCSV) => {
    const lines = rawCSV.split('\n');
    // Find header row (first line containing Test_Case_Id)
    const headerIdx = lines.findIndex(l => l.trim().startsWith('Test_Case_Id'));
    if (headerIdx < 0) return rawCSV; // no header found, return as-is

    const cleanLines = [lines[headerIdx]];
    for (let i = headerIdx + 1; i < lines.length; i++) {
      const trimmed = lines[i].trim();
      if (!trimmed) continue;
      // Valid data rows start with a numeric Test_Case_Id followed by a comma
      if (/^\d+,/.test(trimmed)) {
        cleanLines.push(lines[i]);
      }
    }
    return cleanLines.join('\n');
  };

  // Extract expanded_request_data.csv - use \s* between heading and fence (matches old working code)
  let requestMatch = content.match(/### expanded_request_data\.csv\s*```csv\s*([\s\S]*?)\s*```/i);
  if (!requestMatch) {
    requestMatch = content.match(/## expanded_request_data\.csv\s*```csv\s*([\s\S]*?)\s*```/i);
  }
  if (!requestMatch) {
    requestMatch = content.match(/expanded_request_data\.csv\s*```csv\s*([\s\S]*?)\s*```/i);
  }
  if (!requestMatch) {
    requestMatch = content.match(/### expanded_request_data\.csv\s*```\s*([\s\S]*?)\s*```/i);
  }
  if (!requestMatch) {
    requestMatch = content.match(/### expanded_request_data\.csv\s*```csv\s*([\s\S]*?)$/i);
  }

  if (requestMatch && requestMatch[1]?.trim()) {
    let requestData = requestMatch[1].trim();
    requestData = requestData.replace(/```\s*$/, '').trim();
    requestData = requestData.replace(/\n---[\s\S]*$/, '').trim();
    requestData = requestData.replace(/\n##[\s\S]*$/, '').trim();
    requestData = cleanCSVContent(requestData);
    files.push({
      filename: 'expanded_request_data.csv',
      content: requestData,
      format: 'csv',
      type: 'request_data'
    });
    logger.info('EXTRACTED expanded_request_data.csv', { rows: requestData.split('\n').length });
  } else {
    logger.warn('NO MATCH FOR expanded_request_data.csv');
  }

  // Extract expanded_response_data.csv - use \s* between heading and fence (matches old working code)
  let responseMatch = content.match(/### expanded_response_data\.csv\s*```csv\s*([\s\S]*?)\s*```/i);
  if (!responseMatch) {
    responseMatch = content.match(/## expanded_response_data\.csv\s*```csv\s*([\s\S]*?)\s*```/i);
  }
  if (!responseMatch) {
    responseMatch = content.match(/expanded_response_data\.csv\s*```csv\s*([\s\S]*?)\s*```/i);
  }
  if (!responseMatch) {
    responseMatch = content.match(/### expanded_response_data\.csv\s*```\s*([\s\S]*?)\s*```/i);
  }
  if (!responseMatch) {
    responseMatch = content.match(/### expanded_response_data\.csv\s*```csv\s*([\s\S]*?)$/i);
  }

  if (responseMatch && responseMatch[1]?.trim()) {
    let responseData = responseMatch[1].trim();
    responseData = responseData.replace(/```\s*$/, '').trim();
    responseData = responseData.replace(/\n---[\s\S]*$/, '').trim();
    responseData = responseData.replace(/\n##[\s\S]*$/, '').trim();
    responseData = cleanCSVContent(responseData);
    files.push({
      filename: 'expanded_response_data.csv',
      content: responseData,
      format: 'csv',
      type: 'response_data'
    });
    logger.info('EXTRACTED expanded_response_data.csv', { rows: responseData.split('\n').length });
  } else {
    logger.warn('NO MATCH FOR expanded_response_data.csv', {
      last500: content.substring(content.length - 500)
    });
  }

  // Try multiple patterns for test_matrix_summary.md
  let summaryMatch = content.match(/### test_matrix_summary\.md\s*```(?:markdown|md)?\s*([\s\S]*?)\s*```/i);
  if (!summaryMatch) {
    summaryMatch = content.match(/## test_matrix_summary\.md\s*```(?:markdown|md)?\s*([\s\S]*?)\s*```/i);
  }
  if (!summaryMatch) {
    summaryMatch = content.match(/test_matrix_summary\.md\s*```(?:markdown|md)?\s*([\s\S]*?)\s*```/i);
  }

  if (summaryMatch) {
    files.push({
      filename: 'test_matrix_summary.md',
      content: summaryMatch[1].trim(),
      format: 'markdown',
      type: 'summary'
    });
    logger.info('EXTRACTED test_matrix_summary.md');
  } else {
    logger.debug('NO MATCH FOR test_matrix_summary.md');
  }

  logger.info('TOTAL PERMUTATION FILES EXTRACTED', { count: files.length });
  return files;
}

  static extractTestConfigFromResponse(content) {
    logger.debug('Extracting test config from content', { preview: content.substring(0, 200) });

    const files = [];
    const sections = content.split(/(?=^### )/m);

    logger.debug('Found sections', { count: sections.length });
    
    for (const section of sections) {
      if (!section.trim()) continue;
      
      const headerMatch = section.match(/^### (.+)$/m);
      
      if (headerMatch) {
        let filename = headerMatch[1].trim();
        logger.debug('Found header', { filename });
        
        if (filename === 'Test Configuration JSON' ||
            /test\s*config(?:uration)?\s*(?:json)?/i.test(filename) ||
            /request[\s_-]payload/i.test(filename) ||
            /json\s*(?:test\s*)?config(?:uration)?/i.test(filename)) {
          filename = 'request_payload.json';

          // Use \s* instead of \n so blank lines / trailing spaces after the fence don't break the match
          const codeBlockMatch = section.match(/```json\s*([\s\S]*?)\s*```/);

          if (codeBlockMatch) {
            const fileContent = codeBlockMatch[1].trim();

            if (fileContent) {
              files.push({
                filename: filename,
                content: fileContent,
                format: 'json',
                type: 'config'
              });
              logger.info('Extracted config file', { filename });
            }
          }
          continue;
        }

        // Use \s* so whitespace/blank lines between fence and content don't break the match
        const codeBlockMatch = section.match(/```[\w]*\s*([\s\S]*?)\s*```/);
        
        if (codeBlockMatch) {
          const fileContent = codeBlockMatch[1].trim();
          
          if (fileContent && filename) {
            files.push({
              filename: filename,
              content: fileContent,
              format: this.getFileFormat(filename),
              type: this.getFileType(filename)
            });
            logger.info('Extracted test file', { filename });
          }
        }
      }
    }
    
    // Alternative parsing if no ### headers found
    if (files.length === 0) {
      logger.debug('No ### headers found, trying ## headers');
      const level2Sections = content.split(/(?=^## )/m);
      
      for (const section of level2Sections) {
        if (!section.trim()) continue;
        
        const subHeaders = section.match(/^### (.+)$/gm);
        const codeBlocks = section.match(/```[\w]*\s*[\s\S]*?\s*```/g);

        if (subHeaders && codeBlocks && subHeaders.length === codeBlocks.length) {
          for (let i = 0; i < subHeaders.length; i++) {
            let filename = subHeaders[i].replace('### ', '').trim();
            const codeBlockContent = codeBlocks[i].replace(/```[\w]*\s*/, '').replace(/\s*```$/, '').trim();
            
            if (filename === 'Test Configuration JSON') {
              filename = 'request_payload.json';
            }
            
            if (filename && codeBlockContent) {
              files.push({
                filename: filename,
                content: codeBlockContent,
                format: this.getFileFormat(filename),
                type: this.getFileType(filename)
              });
              logger.info('Extracted test file (L2)', { filename });
            }
          }
        }
      }
    }
    
    // Last-resort fallback: filename mentioned on the line immediately before a code block.
    // Handles responses where the AI writes "**POST_Login_request_data.csv**" then a code block.
    if (files.length === 0) {
      logger.debug('No structured headers found, trying inline filename + code block pattern');
      const inlinePattern = /(?:^|\n)[*_]{0,2}([\w.\-]+\.(?:json|csv|md))[*_]{0,2}\s*\n```[\w]*\s*([\s\S]*?)\s*```/g;
      let inlineMatch;
      while ((inlineMatch = inlinePattern.exec(content)) !== null) {
        let filename = inlineMatch[1].trim();
        const fileContent = inlineMatch[2].trim();
        if (filename === 'Test Configuration JSON') filename = 'request_payload.json';
        if (filename && fileContent) {
          files.push({
            filename,
            content: fileContent,
            format: this.getFileFormat(filename),
            type: this.getFileType(filename)
          });
          logger.info('Extracted test file (inline)', { filename });
        }
      }
    }

    logger.info('Total test config files extracted', { count: files.length });
    return files;
  }

  static parseTestConfigFilesIncrementally(streamContent, endpoint) {
    const detectedFiles = [];

    // OPTIMIZATION: Build lookup Set upfront O(1) per check instead of O(n) find() per pattern
    const detectedTypeSet = new Set();

    const filePatterns = [
      {
        pattern: /request_payload\.json|Test Configuration/i,
        type: 'config',
        title: 'Test Configuration',
        description: 'Defines how the test is set up, including variables, steps, assertions, and data sources.',
        priority: 1
      },
      {
        pattern: /_request_data\.csv|Request Data/i,
        type: 'request_data',
        title: 'Request Data',
        description: 'Contains input values (such as parameters or IDs) that are sent with the API request during test execution.',
        priority: 2
      },
      {
        pattern: /_response_data\.csv|Expected Response Data/i,
        type: 'response_data',
        title: 'Expected Response Data',
        description: 'Defines the expected API responses for each test case, used to validate actual results.',
        priority: 3
      },
      {
        pattern: /_sample_request\.json|Sample Request/i,
        type: 'sample_request',
        title: 'Sample Request',
        description: 'Example of the JSON body or structure that is sent in the API request.',
        priority: 4
      },
      {
        pattern: /_sample_response\.json|Sample Response/i,
        type: 'sample_response',
        title: 'Sample Response',
        description: 'Example of the JSON body or structure returned by the API in a successful response.',
        priority: 5
      }
    ];

    // OPTIMIZATION: Replace detectedFiles.find(f => f.type === pattern.type) O(n) lookup
    // inside forEach with a Set.has() O(1) check - reduces O(n²) to O(n)
    filePatterns.forEach(pattern => {
      if (pattern.pattern.test(streamContent)) {
        if (!detectedTypeSet.has(pattern.type)) {
          detectedTypeSet.add(pattern.type);
          detectedFiles.push({
            type: pattern.type,
            title: pattern.title,
            description: pattern.description,
            priority: pattern.priority,
            detected: true,
            complete: streamContent.includes(`### ${pattern.title}`) ||
                     streamContent.includes(`## ${pattern.title}`)
          });
        }
      }
    });

    return detectedFiles.sort((a, b) => a.priority - b.priority);
  }

  static parseEndpointsFromResponse(content) {
    const mappings = this.parseEndpointMappingsFromResponse(content);
    if (mappings.length > 0) {
      return mappings;
    }
    
    // Fallback to XML or plain text parsing if needed
    const xmlEndpoints = this.parseXMLEndpoints(content);
    if (xmlEndpoints.length > 0) {
      return xmlEndpoints;
    }
    
    // Original plain text parsing
    const endpoints = [];
    const lines = content.split('\n');
    
    for (const line of lines) {
      const trimmedLine = line.trim();
      const methodMatch = trimmedLine.match(/^(GET|POST|PUT|DELETE|PATCH)\s+(.+)$/i);
      if (methodMatch) {
        const method = methodMatch[1].toUpperCase();
        const rawPath = methodMatch[2].trim();
        const cleanedPath = this.cleanEndpointPath(rawPath);
        
        endpoints.push({
          method: method,
          path: cleanedPath,
          description: `${method} ${cleanedPath}`
        });
      }
    }
    
    return endpoints;
  }

  static parseXMLEndpoints(content) {
    const endpoints = [];
    
    const methodMatches = [...content.matchAll(/<method>(GET|POST|PUT|DELETE|PATCH)<\/method>/gi)];
    const pathMatches = [...content.matchAll(/<path>([^<]+)<\/path>/gi)];
    
    const minLength = Math.min(methodMatches.length, pathMatches.length);
    
    for (let i = 0; i < minLength; i++) {
      const method = methodMatches[i][1].toUpperCase();
      const rawPath = pathMatches[i][1].trim();
      const cleanedPath = this.cleanEndpointPath(rawPath);
      
      if (method && cleanedPath) {
        endpoints.push({
          method: method,
          path: cleanedPath,
          description: `${method} ${cleanedPath}`
        });
      }
    }
    
    return endpoints;
  }

  static getFileFormat(filename) {
    const extension = filename.split('.').pop().toLowerCase();
    switch (extension) {
      case 'json': return 'json';
      case 'csv': return 'csv';
      case 'java': return 'java';
      case 'py': return 'python';
      case 'yaml':
      case 'yml': return 'yaml';
      case 'js': return 'javascript';
      case 'rb': return 'ruby';
      case 'sh': return 'bash';
      case 'properties': return 'properties';
      case 'conf': return 'config';
      case 'xml': return 'xml';
      case 'txt': return 'text';
      case 'feature': return 'gherkin';
      default: return 'text';
    }
  }

  static getFileType(filename) {
    const name = filename.toLowerCase();
    if (name.includes('config') || name.includes('payload')) return 'config';
    if (name.includes('data') || name.includes('.csv')) return 'data';
    if (name.includes('runner') || name.includes('test')) return 'test';
    if (name.includes('feature')) return 'feature';
    return 'file';
  }
}

export default EndpointParser;